/**
 * Global period slice — the single, app-wide source of truth for the
 * currently-selected **Exercice** (fiscal year) and the period scope within it
 * (full year, a single month, or a custom date range).
 *
 * Every dated view (analytics dashboard tabs, financial statements, tax
 * declarations, analytic accounting…) resolves its `fromDate`/`toDate` from
 * this slice through the `useGlobalPeriod` hook, so changing the exercice or
 * the date range once refreshes them everywhere.
 *
 * Persisted (redux-persist) so the selection survives navigation and reloads.
 * On tenant switch / logout the `useGlobalPeriod` hook reconciles a stale
 * `exerciseId` against the freshly-fetched fiscal years and re-initialises.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import types from '@/store/types';

export type GlobalPeriodMode = 'full' | 'month' | 'custom';

export interface GlobalPeriodState {
  /** Selected fiscal year id (from `useFiscalYears()`), or null before init. */
  exerciseId: number | null;
  /** Display name, e.g. "Exercice 2025" (cached so the pill renders pre-hydration). */
  exerciseName: string | null;
  /** Fiscal year bounds (YYYY-MM-DD) — cached fallback before the FY query resolves. */
  exerciseStart: string | null;
  exerciseEnd: string | null;
  /** Scope within the exercice. */
  mode: GlobalPeriodMode;
  /** 'YYYY-MM' calendar month when `mode === 'month'`. */
  monthKey: string | null;
  /** Custom range (YYYY-MM-DD) when `mode === 'custom'`. */
  customStart: string | null;
  customEnd: string | null;
}

export const initialGlobalPeriodState: GlobalPeriodState = {
  exerciseId: null,
  exerciseName: null,
  exerciseStart: null,
  exerciseEnd: null,
  mode: 'full',
  monthKey: null,
  customStart: null,
  customEnd: null,
};

const slice = createSlice({
  name: 'globalPeriod',
  initialState: initialGlobalPeriodState,
  reducers: {
    /** Select an exercice (fiscal year) and scope it to the full year. */
    setExercise: (
      state,
      action: PayloadAction<{
        id: number;
        name: string;
        start: string | null;
        end: string | null;
      }>,
    ) => {
      state.exerciseId = action.payload.id;
      state.exerciseName = action.payload.name;
      state.exerciseStart = action.payload.start;
      state.exerciseEnd = action.payload.end;
      state.mode = 'full';
      state.monthKey = null;
      state.customStart = null;
      state.customEnd = null;
    },
    /** Scope the current exercice to its full year. */
    setFull: (state) => {
      state.mode = 'full';
      state.monthKey = null;
      state.customStart = null;
      state.customEnd = null;
    },
    /** Scope the current exercice to a single calendar month ('YYYY-MM'). */
    setMonth: (state, action: PayloadAction<{ monthKey: string }>) => {
      state.mode = 'month';
      state.monthKey = action.payload.monthKey;
      state.customStart = null;
      state.customEnd = null;
    },
    /** Scope to an arbitrary custom date range (YYYY-MM-DD .. YYYY-MM-DD). */
    setCustom: (
      state,
      action: PayloadAction<{ start: string; end: string }>,
    ) => {
      state.mode = 'custom';
      state.customStart = action.payload.start;
      state.customEnd = action.payload.end;
      state.monthKey = null;
    },
    /** Back to the pristine (un-initialised) state. */
    resetPeriod: () => ({ ...initialGlobalPeriodState }),
  },
  extraReducers: (builder) => {
    // Clear on global RESET (logout) so a new tenant re-initialises cleanly.
    builder.addCase(types.RESET, () => ({ ...initialGlobalPeriodState }));
  },
});

export const { setExercise, setFull, setMonth, setCustom, resetPeriod } =
  slice.actions;

const CONFIG = {
  key: 'leyona:globalPeriod',
  storage,
  whitelist: [
    'exerciseId',
    'exerciseName',
    'exerciseStart',
    'exerciseEnd',
    'mode',
    'monthKey',
    'customStart',
    'customEnd',
  ],
};

export default persistReducer(CONFIG, slice.reducer);
